import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Typography } from '@/components/ui/typography';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BELOW_DESKTOP_QUERY } from '@/config/ui.constants';
import { formatPriceDisplay } from '../../../../util/formatting';
import { PropertyCartItem } from '../types/cart.types';
import ServiceCell from './ServiceCell';
import AccessCell from './AccessCell';
import PropertyCartCard from './PropertyCartCard';
import type { FloorPlanTier } from '../floor-plan/floorPlanOffer';

type PropertyCartTableProps = {
  data: PropertyCartItem[];
  onEditService?: (item: PropertyCartItem) => void;
  onEditAccess?: (item: PropertyCartItem) => void;
  onDelete?: (item: PropertyCartItem) => void;
  mutatingItemIds?: Set<string>;
  readOnly?: boolean;
  /** DV-3204: the free floor plan a line's order will get, if any (cart only). */
  freeFloorPlanFor?: (item: PropertyCartItem) => FloorPlanTier | null;
};

const PropertyCartTable = ({
  data,
  onEditService,
  onEditAccess,
  onDelete,
  mutatingItemIds = new Set(),
  readOnly = false,
  freeFloorPlanFor,
}: PropertyCartTableProps) => {
  // Below lg the lines render as cards instead of the fixed-width table —
  // same switch as DataTable's renderMobileCard slot (Unit 3 PR B).
  const isBelowLg = useMediaQuery(BELOW_DESKTOP_QUERY);
  const hasOrderNumbers = data.some((item) => item.orderNumber);

  const columns = useMemo<ColumnDef<PropertyCartItem>[]>(() => {
    const cols: ColumnDef<PropertyCartItem>[] = [];

    if (readOnly && hasOrderNumbers) {
      cols.push({
        id: 'orderId',
        header: 'Order ID',
        size: 100,
        cell: ({ row }) => (
          <Typography variant="body-medium">
            #{row.original.orderNumber || ''}
          </Typography>
        ),
      });
    }

    cols.push({
      id: 'propertyAddress',
      header: 'Property Address',
      size: 210,
      cell: ({ row }) => {
        const { property } = row.original;
        return (
          <div className="flex max-w-[220px] flex-col">
            <Typography variant="body-medium">
              {property.address1 || ''}
            </Typography>
            {property.unitNumber && (
              <Typography variant="body-medium">
                {property.unitNumber}
              </Typography>
            )}
            <Typography variant="body-medium">
              {property.city || ''}, {property.state || ''}{' '}
              {property.zipCode || ''}
            </Typography>
          </div>
        );
      },
    });

    cols.push({
      id: 'services',
      header: 'Service',
      size: readOnly ? 300 : 380,
      cell: ({ row }) => (
        <ServiceCell
          services={row.original.services}
          item={row.original}
          onEdit={onEditService}
          readOnly={readOnly}
          freeFloorPlan={freeFloorPlanFor?.(row.original)}
        />
      ),
    });

    cols.push({
      id: 'accessInfo',
      header: 'Appointment info',
      size: readOnly ? 300 : 350,
      cell: ({ row }) => (
        <AccessCell
          access={row.original.propertyAccess}
          item={row.original}
          onEdit={onEditAccess}
          readOnly={readOnly}
        />
      ),
    });

    cols.push({
      id: 'total',
      header: 'Total',
      size: 100,
      cell: ({ row }) => (
        <Typography variant="body-medium">
          ${formatPriceDisplay(row.original.price)}
        </Typography>
      ),
    });

    if (!readOnly && onDelete) {
      cols.push({
        id: 'delete',
        header: '',
        size: 60,
        cell: ({ row }) => {
          const item = row.original;
          const label = item.formattedAddress
            ? `Remove ${item.formattedAddress} from cart`
            : 'Remove property from cart';
          return (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-primary"
                aria-label={label}
                onClick={() => onDelete(item)}
              >
                <Trash2 className="h-5 w-5" />
              </Button>
            </div>
          );
        },
      });
    }

    return cols;
  }, [
    readOnly,
    hasOrderNumbers,
    onEditService,
    onEditAccess,
    onDelete,
    freeFloorPlanFor,
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0) return null;

  if (isBelowLg) {
    return (
      <ul
        className="flex flex-col gap-3"
        data-testid="property-cart-card-list"
      >
        {data.map((item) => (
          <li key={item.propertyId}>
            <PropertyCartCard
              item={item}
              onEditService={onEditService}
              onEditAccess={onEditAccess}
              onDelete={onDelete}
              isMutating={mutatingItemIds.has(item.propertyId)}
              readOnly={readOnly}
              showOrderNumber={readOnly && hasOrderNumbers}
              freeFloorPlan={freeFloorPlanFor?.(item)}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="!static !z-auto bg-muted text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  style={{
                    width: header.column.getSize(),
                    minWidth: header.column.getSize(),
                  }}
                >
                  {!header.isPlaceholder &&
                    flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const isMutating = mutatingItemIds.has(row.original.propertyId);
            return (
              <TableRow
                key={row.id}
                className={
                  isMutating ? 'pointer-events-none opacity-60' : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="py-5 align-middle">
                    {cell.column.id === 'delete' && isMutating ? (
                      <div className="flex justify-center">
                        <Spinner size="sm" />
                      </div>
                    ) : (
                      flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )
                    )}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default PropertyCartTable;
